import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Search, User } from "lucide-react";
import { OpportunityRow } from "@/components/opportunity";
import Dashboard from "@/components/pages/dashboard";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useProfileQuery } from "@/queries/user/profile";
import {
  Opportunity,
  useCandidateOpportunities,
} from "@/queries/candidate/opportunities";
import { CandidateJobOpening } from "@/types";
import OpportunityApplyDialog from "@/components/forms/candidate/opportunity/apply";
import { OpportunityDetailsDialog } from "@/components/dialogs/opportunity/details";
// import { useMyCandidateQuery } from "@/queries/candidate/my-candidate";

type Section = "matches" | "all" | "applied" | "active" | "closed";

const normalFilters: { label: string; value: Section }[] = [
  { label: "Your Matches", value: "matches" },
  { label: "Applied", value: "applied" },
  { label: "Active", value: "active" },
  { label: "Closed", value: "closed" },
];

// const allFilters: { label: string; value: Section }[] = [
//   { label: "Your Matches", value: "matches" },
//   { label: "All Opportunities", value: "all" },
//   { label: "Applied", value: "applied" },
//   { label: "Active", value: "active" },
//   { label: "Closed", value: "closed" },
// ];

const isMatch = ({ status, candidateStatus }: Opportunity) =>
  status !== "Role Filled (lost)" &&
  status !== "Role Filled (won)" &&
  candidateStatus &&
  candidateStatus.status === "Checking Interest";

const isApplied = ({ status, candidateStatus }: Opportunity) =>
  status !== "Role Filled (lost)" &&
  status !== "Role Filled (won)" &&
  candidateStatus?.status &&
  [
    "Screening",
    "Possible Fit",
    "Shortlisted",
    "Making Offer",
    "Contracting",
  ].includes(candidateStatus.status);

const isActive = ({ candidateStatus }: Opportunity) =>
  candidateStatus && candidateStatus.status === "Accepted by Client";

const isClosed = ({ status, candidateStatus }: Opportunity) =>
  // User was a candidate
  candidateStatus &&
  // And the role was lost
  (status === "Role Filled (lost)" ||
    // Or the candidate was rejected/passed
    (candidateStatus.status &&
      ["Not Interested", "Rejected for Role"].includes(
        candidateStatus.status,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      )) ||
    // Or the candidate did not win the role
    (status === "Role Filled (won)" &&
      candidateStatus.status !== "Accepted by Client"));

const isOpen = ({ status, candidateStatus }: Opportunity) =>
  status !== "Role Filled (lost)" &&
  status !== "Role Filled (won)" &&
  !candidateStatus;

const CandidateOpportunities = () => {
  const { profile, profileLoading } = useProfileQuery();
  // const { candidate } = useMyCandidateQuery();

  const { candidateOpportunities, candidateOpportunitiesLoading } =
    useCandidateOpportunities();

  const [section, setSection] = useState<Section>("applied");
  const [autoSectionChosen, setAutoSectionChosen] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [activeOpportunity, setActiveOpportunity] =
    useState<Opportunity | null>(null);
  const [activeOpportunityDetails, setActiveOpportunityDetails] =
    useState<Opportunity | null>(null);

  const handleApplyClick = (opportunity: Opportunity) => {
    setActiveOpportunity(opportunity);
  };

  const handleDetailsClick = (opportunity: Opportunity) => {
    setActiveOpportunityDetails(opportunity);
  };

  useEffect(() => {
    if (autoSectionChosen || candidateOpportunitiesLoading || profileLoading) {
      return;
    }

    if (candidateOpportunities?.some(isMatch)) {
      setSection("matches");
      // } else if (profile?.biddingEnabled) {
      //   setSection("all");
    } else if (candidateOpportunities?.some(isApplied)) {
      setSection("applied");
    } else {
      setSection("active");
    }

    setAutoSectionChosen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profile,
    candidateOpportunities,
    profileLoading,
    candidateOpportunitiesLoading,
  ]);

  useEffect(() => {
    if (activeOpportunity) {
      const newOpportunity = candidateOpportunities?.find(
        (opportunity) => opportunity.id === activeOpportunity.id,
      );
      if (newOpportunity) {
        setActiveOpportunity(newOpportunity);
      }
    }

    if (activeOpportunityDetails) {
      const newOpportunity = candidateOpportunities?.find(
        (opportunity) => opportunity.id === activeOpportunityDetails.id,
      );
      if (newOpportunity) {
        setActiveOpportunityDetails(newOpportunity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateOpportunities]);

  // const filters = useMemo(
  //   () =>
  //     // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  //     profile?.userType.includes("Admin") ||
  //     (candidate?.applicationComplete && profile?.biddingEnabled)
  //       ? allFilters
  //       : normalFilters,
  //   [profile, candidate?.applicationComplete],
  // );

  const filters = normalFilters;

  const filteredOpportunities = useMemo(() => {
    if (!candidateOpportunities) {
      return [];
    }

    let sectionOpportunities: CandidateJobOpening[];
    if (section === "matches") {
      sectionOpportunities = candidateOpportunities.filter(isMatch);
    } else if (section === "applied") {
      sectionOpportunities = candidateOpportunities.filter(isApplied);
    } else if (section === "active") {
      sectionOpportunities = candidateOpportunities.filter(isActive);
    } else if (section === "closed") {
      sectionOpportunities = candidateOpportunities.filter(isClosed);
    } else {
      sectionOpportunities = candidateOpportunities.filter(isOpen);
    }

    if (searchString) {
      const filterString = searchString.toLowerCase();
      return sectionOpportunities.filter(
        ({ company, role }) =>
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          role?.toLowerCase().includes(filterString) ||
          company?.name?.toLowerCase().includes(filterString),
      );
    }

    return sectionOpportunities;
  }, [candidateOpportunities, section, searchString]);

  const sectionCountMap: Record<Section, number> = useMemo(
    () => ({
      matches:
        candidateOpportunities?.reduce(
          (acc, cur) => (isMatch(cur) ? acc + 1 : acc),
          0,
        ) ?? 0,
      all:
        candidateOpportunities?.reduce(
          (acc, cur) => (isOpen(cur) ? acc + 1 : acc),
          0,
        ) ?? 0,
      applied:
        candidateOpportunities?.reduce(
          (acc, cur) => (isApplied(cur) ? acc + 1 : acc),
          0,
        ) ?? 0,
      active:
        candidateOpportunities?.reduce(
          (acc, cur) => (isActive(cur) ? acc + 1 : acc),
          0,
        ) ?? 0,
      closed:
        candidateOpportunities?.reduce(
          (acc, cur) => (isClosed(cur) ? acc + 1 : acc),
          0,
        ) ?? 0,
    }),
    [candidateOpportunities],
  );

  return (
    <Dashboard.Root>
      <Dashboard.ProfileRow>
        <Dashboard.ProfileInfo
          title="Opportunities"
          description={
            <p className="text-sm md:text-base text-muted-foreground">
              Explore and bid on new project opportunities tailored to your
              expertise
            </p>
          }
          fallbackIcon={User}
          src={profile?.profilePictureLink}
          loading={profileLoading}
        />
      </Dashboard.ProfileRow>
      <Dashboard.MainContent className="md:col-span-2 md:col-start-1">
        <div className="flex flex-col gap-2 md:gap-0 md:border md:border-black md:border-opacity-10 md:rounded-md">
          <ToggleGroup
            type="single"
            value={section}
            onValueChange={(value) => setSection(value as Section)}
            className="hidden md:inline-flex w-full rounded-none"
          >
            {filters.map((filter) => (
              <ToggleGroupItem
                key={`toggle-${filter.value}`}
                value={filter.value}
              >
                {filter.label}
                {sectionCountMap[filter.value] > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs px-1 w-fit min-w-5 h-5">
                    {sectionCountMap[filter.value] > 20
                      ? "20+"
                      : sectionCountMap[filter.value]}
                  </span>
                )}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <div className="block md:hidden w-full">
            <Select
              value={section}
              onValueChange={(value) => setSection(value as Section)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {filters.map((filter) => (
                  <SelectItem
                    key={`select-${filter.value}`}
                    value={filter.value}
                  >
                    {filter.label}
                    {sectionCountMap[filter.value] > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs px-1 w-fit h-5">
                        {sectionCountMap[filter.value] > 20
                          ? "20+"
                          : sectionCountMap[filter.value]}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex p-2 self-stretch md:border-t md:border-black md:border-opacity-10 md:bg-[#f6f6f6]">
            <div
              className={cn(
                "flex-1 md:flex-initial flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                name="searchString"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base bg-transparent",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for a role"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <OpportunitiesSection
          opportunities={filteredOpportunities}
          handleApplyClick={handleApplyClick}
          handleDetailsClick={handleDetailsClick}
        />
        {activeOpportunity ? (
          <OpportunityApplyDialog
            opening={activeOpportunity}
            open={true}
            setOpen={(open: boolean) => !open && setActiveOpportunity(null)}
          />
        ) : null}
        {activeOpportunityDetails ? (
          <OpportunityDetailsDialog
            opening={activeOpportunityDetails}
            open={true}
            setOpen={(open: boolean) =>
              !open && setActiveOpportunityDetails(null)
            }
          />
        ) : null}
      </Dashboard.MainContent>
    </Dashboard.Root>
  );
};

export default CandidateOpportunities;

const OpportunitiesSection = ({
  opportunities,
  handleApplyClick,
  handleDetailsClick,
}: {
  opportunities: Opportunity[];
  handleApplyClick: (o: Opportunity) => void;
  handleDetailsClick: (o: Opportunity) => void;
}) => {
  return (
    <Dashboard.Collapsible defaultOpen>
      <Dashboard.CollapsibleContent>
        {opportunities.length > 0 ? (
          <div className="w-full flex flex-col rounded-lg border">
            {opportunities.map((opportunity) => (
              <OpportunityRow
                key={opportunity.id}
                opportunity={opportunity}
                handleApplyClick={handleApplyClick}
                handleDetailsClick={handleDetailsClick}
              />
            ))}
          </div>
        ) : (
          <p className="w-full text-sm text-muted-foreground text-center">
            No opportunities found. Please check back later.
          </p>
        )}
      </Dashboard.CollapsibleContent>
    </Dashboard.Collapsible>
  );
};
